/**
 * 하이라이트 결과를 **조각 목록**으로 편다 — `dangerouslySetInnerHTML` 없이 그리기 위한 것.
 *
 * ## 왜 문자열을 다시 푸는가
 *
 * 하이라이터(`highlightTextWithHtmlRemoval`)의 «찾는 규칙»은 간단하지 않다 — 구절 매칭,
 * 토큰 사이 구분자 허용, `@#` 접두, 중복 토큰 제거, 우선순위 다섯. 그것을 노드 방식으로
 * **다시 구현하면 규칙이 조용히 갈린다.**
 *
 * 그래서 매칭은 **그대로 쓰고**, 그 결과 문자열만 조각으로 되편다. 세척을 먼저 지나온
 * 입력이라 그 문자열에 있는 태그는 **하이라이터가 스스로 넣은 둘뿐**이다 —
 * `<mark style="…">` 과 `<span style="…">`. 모양이 닫혀 있으니 파싱이 정의된다.
 *
 * ⚠️ 이 파서는 **범용 HTML 파서가 아니다.** 아는 태그 둘만 읽고 나머지는 글자로 본다.
 * 그것이 안전한 이유이기도 하다 — 모르는 것을 만나면 «태그로 해석하지 않는다».
 */

/** 이름 엔티티 여섯 — 텍스트 노드로 나갈 값만 지난다 */
const NAMED: Record<string, string> = {
  '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ',
};

/**
 * 🚨 **텍스트 노드 전용 되돌리기.**
 *
 * DOMPurify 는 «HTML 문자열»을 돌려주므로 글자가 escape 돼 있다(`&amp;`). 그것을 React
 * 텍스트 노드로 그대로 넣으면 화면에 「&amp;」가 보인다. 그래서 여기서 되돌린다.
 *
 * ⚠️ **이 결과를 `dangerouslySetInnerHTML` 에 넣지 마라** — 되돌린 값에는 `<` 가 글자로
 * 들어 있을 수 있고, HTML 로 주입하는 순간 그것이 태그가 된다.
 * 쓰임은 React 텍스트 노드 하나뿐이고, 그때는 React 가 다시 escape 하므로 안전하다.
 */
function decodeEntitiesForText(input: string): string {
  return input
    .replace(/&(?:amp|lt|gt|quot|#39|nbsp);/g, (m) => NAMED[m] ?? m)
    .replace(/&#(\d+);/g, (whole, code: string) => {
      const n = Number(code);
      if (!Number.isFinite(n) || n <= 0 || n > 0x10ffff) return whole;
      try { return String.fromCodePoint(n); } catch { return whole; }
    })
    .replace(/&#x([0-9a-fA-F]+);/g, (whole, hex: string) => {
      const n = parseInt(hex, 16);
      if (!Number.isFinite(n) || n <= 0 || n > 0x10ffff) return whole;
      try { return String.fromCodePoint(n); } catch { return whole; }
    });
}

/** 하이라이터가 넣는 두 모양만 인식한다 */
const KNOWN_TAG = /(<mark style="[^"]*">|<\/mark>|<span style="[^"]*">|<\/span>)/g;
const OPEN_MARK = /^<mark style="([^"]*)">$/;
const OPEN_SPAN = /^<span style="([^"]*)">$/;

/** 그릴 조각 하나 — `style` 이 없으면 맨 글자다 */
export type HighlightSegment = {
  text: string;
  /** `mark`(배경 강조) · `span`(글자색) · `null`(강조 없음) */
  tag: 'mark' | 'span' | null;
  /** 하이라이터가 붙인 인라인 스타일 문자열 그대로 */
  style: string | null;
};

/**
 * 하이라이트된 문자열 → 조각 목록.
 *
 * 겹침(`<mark>` 안의 `<span>`)은 **안쪽이 이긴다** — 화면에서도 안쪽 스타일이 보이던 모양
 * 그대로다. 우선순위를 새로 정하지 않으려고 «가장 안쪽 열린 태그»를 그 조각의 tag 로 삼는다.
 */
export function toHighlightSegments(html: string | null | undefined): HighlightSegment[] {
  if (typeof html !== 'string' || html === '') return [];

  const out: HighlightSegment[] = [];
  /** 열린 태그 스택 — 가장 뒤가 «가장 안쪽» */
  const open: { tag: 'mark' | 'span'; style: string }[] = [];

  const pushText = (raw: string) => {
    if (!raw) return;
    const innermost = open.length > 0 ? open[open.length - 1] : null;
    out.push({
      // 🔑 여기서 엔티티를 푼다 — 이 값은 **텍스트 노드로만** 나간다(위 `decodeEntitiesForText` 의 🚨 참조).
      text: decodeEntitiesForText(raw),
      tag: innermost?.tag ?? null,
      style: innermost?.style ?? null,
    });
  };

  html.split(KNOWN_TAG).forEach((piece) => {
    if (!piece) return;

    const mark = OPEN_MARK.exec(piece);
    if (mark) { open.push({ tag: 'mark', style: mark[1] }); return; }

    const span = OPEN_SPAN.exec(piece);
    if (span) { open.push({ tag: 'span', style: span[1] }); return; }

    if (piece === '</mark>' || piece === '</span>') {
      const wanted = piece === '</mark>' ? 'mark' : 'span';
      // 짝이 맞는 가장 안쪽 것을 닫는다. 안 맞으면 무시한다(글자로 새지 않게)
      for (let i = open.length - 1; i >= 0; i -= 1) {
        if (open[i].tag === wanted) { open.splice(i, 1); return; }
      }
      return;
    }

    pushText(piece);
  });

  return out;
}

/** 조각에 강조가 하나라도 있나 — 없으면 화면이 그냥 문자열로 그릴 수 있다 */
export function hasHighlight(segments: readonly HighlightSegment[]): boolean {
  return segments.some((s) => s.tag !== null);
}
